import { merchantChartColors } from 'utils/StaticData';

// Generate colors for merchant lines
export const generateMerchantColors = (merchantCount) => {
  const colors = [...merchantChartColors]; // Use colors from StaticData

  // If we need more colors than predefined, generate them
  if (merchantCount > colors.length) {
    for (let i = colors.length; i < merchantCount; i++) {
      const hue = (i * 137.508) % 360; // Golden angle approximation for good color distribution
      colors.push(`hsl(${hue}, 70%, 60%)`);
    }
  }

  return colors.slice(0, merchantCount);
};

// Process merchant data for chart rendering
export const processMerchantData = (grossRevenueData) => {
  if (!grossRevenueData || !Array.isArray(grossRevenueData)) {
    return { processedData: [], merchantList: [], merchantColors: {} };
  }

  // Extract unique merchants from all data points
  const merchantSet = new Set();
  grossRevenueData.forEach((item) => {
    if (item.merchants && Array.isArray(item.merchants)) {
      item.merchants.forEach((merchant) => {
        merchantSet.add(`${merchant.merchantId}-${merchant.name}`);
      });
    }
  });

  const merchantList = Array.from(merchantSet).map((merchantKey) => {
    const [merchantId, name] = merchantKey.split('-');
    return { merchantId: parseInt(merchantId), name };
  });

  // Generate colors for merchants
  const colors = generateMerchantColors(merchantList.length);
  const merchantColors = {};
  merchantList.forEach((merchant, index) => {
    merchantColors[merchant.merchantId] = colors[index];
  });

  // Process data to include merchant totals for each time point
  const processedData = grossRevenueData.map((item) => {
    const processedItem = { ...item };

    // Add merchant data as separate properties for chart rendering
    if (item.merchants && Array.isArray(item.merchants)) {
      item.merchants.forEach((merchant) => {
        // Convert merchant total to number (handles both string and number values)
        processedItem[`merchant_${merchant.merchantId}`] = Number(
          merchant.total,
        );
      });
    }

    return processedItem;
  });

  return { processedData, merchantList, merchantColors };
};

// Calculate commission amount based on merchant filtering logic
export const calculateCommissionAmount = (
  sellerCommissionData,
  selectedMerchant,
  merchantNameList,
  revenueOverviewData,
) => {
  // Handle new API response structure where data is nested under data.merchants
  const merchantsData = sellerCommissionData?.merchants || sellerCommissionData;

  if (!merchantsData || !Array.isArray(merchantsData)) {
    return Number(revenueOverviewData.commissionAmount) || 0;
  }

  // If selectedMerchant has values (array): Display the sum of commissions for selected merchants
  if (
    selectedMerchant &&
    Array.isArray(selectedMerchant) &&
    selectedMerchant.length > 0
  ) {
    const selectedMerchantIds = selectedMerchant.map((merchant) =>
      String(merchant.merchantId),
    );
    return merchantsData
      .filter((item) => selectedMerchantIds.includes(String(item.merchantId)))
      .reduce((total, item) => total + (Number(item.commission) || 0), 0);
  }

  // If selectedMerchant is a single value (backward compatibility): Display only the commission for that specific merchant ID
  if (selectedMerchant && !Array.isArray(selectedMerchant)) {
    const merchantCommission = merchantsData.find(
      (item) => String(item.merchantId) === String(selectedMerchant),
    );
    return merchantCommission ? Number(merchantCommission.commission) : 0;
  }

  // If selectedMerchant is empty/null: Display the sum of commissions for all merchants listed in merchantNameList
  if (merchantNameList && merchantNameList.length > 0) {
    const merchantIds = merchantNameList.map((merchant) =>
      String(merchant.merchantId),
    );
    return merchantsData
      .filter((item) => merchantIds.includes(String(item.merchantId)))
      .reduce((total, item) => total + (Number(item.commission) || 0), 0);
  }

  // If merchantNameList is empty or undefined: Display the sum of all merchants' commission values
  return merchantsData.reduce(
    (total, item) => total + (Number(item.commission) || 0),
    0,
  );
};

// Calculate commission rate based on merchant selection
export const getCommissionRate = (
  sellerCommissionData,
  selectedMerchant,
  revenueOverviewData,
) => {
  // Handle new API response structure where data is nested under data.merchants
  const merchantsData = sellerCommissionData?.merchants || sellerCommissionData;

  // If no seller commission data, fall back to revenueOverviewData
  if (!sellerCommissionData) {
    return revenueOverviewData.commission;
  }

  // If selectedMerchant is an array with values: Display average commission rate for selected merchants
  if (
    selectedMerchant &&
    Array.isArray(selectedMerchant) &&
    selectedMerchant.length > 0 &&
    merchantsData &&
    Array.isArray(merchantsData)
  ) {
    const selectedMerchantIds = selectedMerchant.map((merchant) =>
      String(merchant.merchantId),
    );
    const selectedMerchantCommissions = merchantsData.filter((item) =>
      selectedMerchantIds.includes(String(item.merchantId)),
    );

    if (selectedMerchantCommissions.length > 0) {
      const totalRate = selectedMerchantCommissions.reduce(
        (sum, item) => sum + (item.commissionRate || 0),
        0,
      );
      return (totalRate / selectedMerchantCommissions.length).toFixed(2);
    }
  }

  // If selectedMerchant is a single value (backward compatibility): Display that merchant's commission rate
  if (
    selectedMerchant &&
    !Array.isArray(selectedMerchant) &&
    merchantsData &&
    Array.isArray(merchantsData)
  ) {
    const merchantCommission = merchantsData.find(
      (item) => String(item.merchantId) === String(selectedMerchant),
    );
    if (merchantCommission && merchantCommission.commissionRate) {
      return merchantCommission.commissionRate;
    }
  }

  // If no merchant selected or merchant not found: Display global commission rate
  if (sellerCommissionData?.commissionRate) {
    return sellerCommissionData.commissionRate;
  }

  // Final fallback to revenueOverviewData
  return revenueOverviewData.commission;
};
